import * as React from "react";
import { PCSTable, IColumnDefinition } from "../PCSTable";
import { CustomerInvoice } from "@/services/api/models/CustomerInvoice";
import { format } from "date-fns";
import { CurrencyCell } from "../../atoms/CurrencyCell/CurrencyCell";
import {
	Button,
	Dialog,
	DialogActions,
	DialogBody,
	DialogContent,
	DialogSurface,
	DialogTitle,
	DialogTrigger,
	mergeClasses,
} from "@fluentui/react-components";
import {
	DocumentBulletList24Regular as DocumentBulletListRegular,
	OpenFilled,
	ArrowDownloadFilled,
} from "@fluentui/react-icons";
import styles from "./InvoiceTable.module.scss";
import { Loading } from "../../molecules/Loading/Loading";
import { useInvoiceFile } from "@/hooks/Invoices";
import { MessageBar } from "../../molecules/MessageBar/MessageBar";
export interface IInvoicesTableProps {
	invoiceItems: CustomerInvoice[];
}

export function InvoicesTable(props: IInvoicesTableProps) {
	const [selectedInvoice, setSelectedInvoice] = React.useState<CustomerInvoice | null>(null);

	const columns: IColumnDefinition<CustomerInvoice>[] = React.useMemo<
		IColumnDefinition<CustomerInvoice>[]
	>(() => {
		return [
			{
				columnId: "customerRO",
				headerText: "RO #",
				getValue: (item) => item.customerRO,
				sizing: {
					defaultWidth: 50,
				},
				sortable: true,
			},
			{
				columnId: "invoiceDate",
				headerText: "Invoice Date",
				getValue: (item) => item.invoiceDate,
				onRender: (item) => item.invoiceDate && format(item.invoiceDate, "MM/dd/yyyy"),
				sortable: true,
				sizing: {
					defaultWidth: 50,
				},
			},
			{
				columnId: "pcsCompleted",
				headerText: "PCS Completed",
				getValue: (item) => item.roCompleted,
				onRender: (item) => item.roCompleted && format(item.roCompleted, "MM/dd/yyyy"),
				sortable: true,
				sizing: {
					defaultWidth: 50,
				},
			},

			{
				columnId: "vehicle",
				headerText: "Vehicle",
				getValue: (item) => item.vehicle,
				sortable: true,
				filterable: true,
			},
			{
				columnId: "vin",
				headerText: "VIN",
				getValue: (item) => item.vin,
				sortable: true,
				filterable: true,
			},

			// {
			// 	columnId: "tool",
			// 	headerText: "Tool",
			// },

			{
				columnId: "invoiceTotal",
				headerText: "Invoice $",
				getValue: (item) => item.invoiceAmount,
				sortable: true,
				sizing: {
					defaultWidth: 50,
				},
				onRender(item) {
					return <CurrencyCell value={item.invoiceAmount ?? 0} />;
				},
			},
			// {
			// 	columnId: "rebate",
			// 	headerText: "Rebate",
			// },
			{
				columnId: "roNumber",
				headerText: "PCS RO #",
				getValue: (item) => item.roNumber,
				sizing: {
					defaultWidth: 50,
				},
				sortable: true,
			},
			{
				columnId: "invoiceNumber",
				headerText: "Invoice #",
				getValue: (item) => item.invoiceNumber,
				sortable: true,
			},
			{
				columnId: "servicesProvided",
				headerText: "Services Provided",
				getValue: (item) => item.service,
				filterable: true,
				sortable: true,
			},
			{
				columnId: "documents",
				headerText: "Documents",
				getValue: (item) => (item.hasInvoiceFile ? "Yes" : "No"),
				sizing: {
					defaultWidth: 50,
				},
				onRender: (item) => (
					<Button
						appearance="transparent"
						icon={<DocumentBulletListRegular />}
						onClick={() => setSelectedInvoice(item)}></Button>
				),
			},
		];
	}, []);

	return (
		<div>
			<PCSTable
				defaultSortState={{ sortColumn: "invoiceDate", sortDirection: "descending" }}
				items={props.invoiceItems}
				searchable
				columns={columns}></PCSTable>
			<InvoiceFileDialog
				item={selectedInvoice}
				isOpen={!!selectedInvoice}
				onClose={() => setSelectedInvoice(null)}
			/>
		</div>
	);
}

interface IInvoiceFileDialogProps {
	item: CustomerInvoice | null | undefined;
	isOpen: boolean;
	onClose?: () => void;
}

function InvoiceFileDialog({ item, isOpen, onClose }: IInvoiceFileDialogProps) {
	const url = React.useMemo(
		() => (item ? `/api/invoice/${item.customerId}/${item.id}/file` : null),
		[item]
	);
	const [showError, setShowError] = React.useState(false);
	const {
		data: invoiceFile,
		error,
		isLoading,
		isValidating,
	} = useInvoiceFile(item?.customerId, item?.id);
	const objectUrl = React.useMemo(() => {
		return invoiceFile ? URL.createObjectURL(invoiceFile) : null;
	}, [invoiceFile]);

	return (
		<div>
			<Dialog
				open={isOpen}
				modalType="non-modal"
				onOpenChange={(ev, state) => {
					if (!state.open) {
						onClose?.();
					}
				}}>
				<DialogSurface className={styles.dialogBody}>
					<DialogBody>
						<DialogTitle>{item?.invoiceNumber}</DialogTitle>
						<DialogContent>
							<div className={styles.dialogContent}>
								<div className={styles.error}>
									<MessageBar
										hidden={!error?.message}
										message={"There was a problem loading the invoice file."}
										dismissable={false}
										buttonText={showError ? "Show Less" : "Show More"}
										onClick={() => setShowError(!showError)}
										messageType="error"
									/>
									{showError && <p>{error?.message}</p>}
								</div>
								{objectUrl && (
									<iframe
										className={mergeClasses(styles.pdfViewer, isLoading && styles.loading)}
										src={`${objectUrl}#toolbar=0`}
									/>
								)}
								{isLoading && !objectUrl && !error && <Loading />}
							</div>
						</DialogContent>
						<DialogActions fluid>
							<Button
								disabled={!!error || isLoading}
								icon={<ArrowDownloadFilled />}
								as="a"
								appearance="primary"
								target="_blank"
								href={`${url}?download=1`}>
								Download
							</Button>
							<Button
								disabled={!!error || isLoading}
								icon={<OpenFilled />}
								as="a"
								appearance="secondary"
								target="_blank"
								href={`${url}`}>
								Open
							</Button>
							<DialogTrigger disableButtonEnhancement>
								<Button appearance="secondary">Close</Button>
							</DialogTrigger>
						</DialogActions>
					</DialogBody>
				</DialogSurface>
			</Dialog>
		</div>
	);
}
